/**
 * Environment access.
 *
 * Vite exposes env vars prefixed with VITE_ on `import.meta.env`. We type
 * them here so the rest of the codebase reads them through a typed surface
 * rather than scattered `import.meta.env.X` calls.
 */

/** Default X-Tenant-Slug when URL/auth do not resolve a tenant. Override via VITE_TENANT_SLUG. */
export const DEFAULT_TENANT_SLUG = "tnn";

interface AppEnv {
  apiBaseUrl: string;
  apexDomain: string;
  /** Default tenant slug for X-Tenant-Slug when URL/auth do not resolve one. */
  tenantSlug: string;
  demoTenantSlug: string;
  isDev: boolean;
  isProd: boolean;
  enableMockData: boolean;
  /** QA quick-login panel on the login page (staging only). */
  showQaAccounts: boolean;
  /** Shared QA password for one-click login (must match backend TNN_QA_DEFAULT_PASSWORD). */
  qaDefaultPassword: string;
}

/** Typed accessor for the VITE_-prefixed environment variables. */
export const env: AppEnv = {
  // Default empty so dev uses Vite's proxy (configured in vite.config.ts).
  // In production, set VITE_API_BASE_URL to the absolute API URL.
  apiBaseUrl: import.meta.env.VITE_API_BASE_URL ?? "",
  // The apex domain tenant subdomains live under. MUST match the backend's
  // TNN_APEX_DOMAIN setting - both sides strip this apex off the host to
  // recover the tenant slug, and if they disagree, tenant resolution
  // silently fails. Override per environment via VITE_APEX_DOMAIN.
  apexDomain: import.meta.env.VITE_APEX_DOMAIN ?? "tnn.new901.io",
  // Sent as X-Tenant-Slug on every API request when the URL and auth
  // state do not already identify a tenant. Defaults to tnn;
  // override per environment via VITE_TENANT_SLUG.
  tenantSlug:
    (import.meta.env.VITE_TENANT_SLUG as string | undefined)?.trim() ||
    DEFAULT_TENANT_SLUG,
  // The slug of the demo tenant shown via the control deployment's
  // demo-tenant portal. Only meaningful on a control deployment.
  demoTenantSlug: import.meta.env.VITE_DEMO_TENANT_SLUG ?? "memphis-rox",
  isDev: import.meta.env.DEV,
  isProd: import.meta.env.PROD,
  enableMockData: import.meta.env.VITE_ENABLE_MOCK_DATA === "true",
  showQaAccounts: import.meta.env.VITE_SHOW_QA_ACCOUNTS === "true",
  qaDefaultPassword:
    (import.meta.env.VITE_QA_DEFAULT_PASSWORD as string | undefined)?.trim() ||
    "tnnqa2026",
};
